import type { PdfPageInfo } from '../../pipeline/pdf';
import type { TextDetection, PiiType } from '../../detect/pii/types';
import type {
  TextEntry,
  ManualEntry,
  ManualTextEntry,
  ImageTextEntry,
} from '../redactionStore';
import { itemRectForCharRange } from './render';
import { PII_LABEL } from '../../detect/pii/types';

/** A clickable / hoverable redaction overlay region. Coordinates are
 *  normalized [0..1] of the canvas so they survive layout / scale
 *  changes. The hover layer multiplies by getBoundingClientRect() at
 *  hit-test time. */
export type Hotspot = {
  /** Bounding box in normalised [0..1] canvas-relative coords. */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Entry ID this hotspot represents — used to toggle off when the
   *  user clicks. */
  entryId: string;
  /** Short text shown in the hover tooltip. The redaction's actual
   *  content for text PII, the type name for opaque ones. */
  label: string;
  /** Secondary type label ("name", "address", "manual region") shown
   *  beneath the primary label. */
  typeLabel: string;
};

const labelForType = (t: PiiType): string => PII_LABEL[t];

/**
 * Build hotspots for the text-PII redactions on a PDF page. Mirrors
 * the geometry that drawTextDetections uses to paint the rects, so
 * the hover region lines up with the visible black box exactly.
 *
 * Multi-item detections (a phone number split across two pdfjs text
 * items, etc.) produce multiple hotspots sharing the same entryId.
 * Hover over either piece highlights / toggles the whole detection.
 */
export const hotspotsForTextEntries = (
  page: PdfPageInfo,
  entries: readonly TextEntry[],
  scale: number,
  canvasWidth: number,
  canvasHeight: number,
): Hotspot[] => {
  const out: Hotspot[] = [];
  if (canvasWidth <= 0 || canvasHeight <= 0) return out;
  for (const entry of entries) {
    const d = entry.detection;
    for (const item of page.items) {
      if (d.end <= item.start || d.start >= item.end) continue;
      const rect = itemRectForCharRange(
        item,
        Math.max(d.start, item.start),
        Math.min(d.end, item.end),
        page.heightPdf,
        scale,
      );
      if (rect.w <= 0 || rect.h <= 0) continue;
      out.push({
        x: rect.x / canvasWidth,
        y: rect.y / canvasHeight,
        w: rect.w / canvasWidth,
        h: rect.h / canvasHeight,
        entryId: entry.id,
        label: d.text,
        typeLabel: labelForType(d.type),
      });
    }
  }
  return out;
};

/** Hotspots for raw text detections that don't have entry IDs yet —
 *  used by the image-redaction path where the underlying entries are
 *  image-text but the labels come from imageTextHits. Same shape, no
 *  toggle support. */
export const hotspotsForTextDetections = (
  page: PdfPageInfo,
  detections: readonly TextDetection[],
  scale: number,
  canvasWidth: number,
  canvasHeight: number,
): Hotspot[] => {
  const out: Hotspot[] = [];
  if (canvasWidth <= 0 || canvasHeight <= 0) return out;
  for (const d of detections) {
    for (const item of page.items) {
      if (d.end <= item.start || d.start >= item.end) continue;
      const rect = itemRectForCharRange(
        item,
        Math.max(d.start, item.start),
        Math.min(d.end, item.end),
        page.heightPdf,
        scale,
      );
      if (rect.w <= 0 || rect.h <= 0) continue;
      out.push({
        x: rect.x / canvasWidth,
        y: rect.y / canvasHeight,
        w: rect.w / canvasWidth,
        h: rect.h / canvasHeight,
        entryId: '',
        label: d.text,
        typeLabel: labelForType(d.type),
      });
    }
  }
  return out;
};

/**
 * Build hotspots for manual rects, manual text-highlights, and
 * OCR-derived image-text hits. Rects are stored normalised already,
 * so we just pass them through and attach the entry id + label.
 */
export const hotspotsForManualEntries = (
  entries: readonly (ManualEntry | ManualTextEntry | ImageTextEntry)[],
): Hotspot[] => {
  const out: Hotspot[] = [];
  for (const entry of entries) {
    if (entry.kind === 'manual') {
      const r = entry.rect;
      if (r.w <= 0 || r.h <= 0) continue;
      out.push({
        x: r.x,
        y: r.y,
        w: r.w,
        h: r.h,
        entryId: entry.id,
        label: 'manual redaction',
        typeLabel: 'drag rectangle',
      });
    } else if (entry.kind === 'manual-text') {
      for (const r of entry.rects) {
        if (r.w <= 0 || r.h <= 0) continue;
        out.push({
          x: r.x,
          y: r.y,
          w: r.w,
          h: r.h,
          entryId: entry.id,
          label: entry.text,
          typeLabel: 'highlighted text',
        });
      }
    } else {
      // image-text — OCR-derived hit on an image / scanned PDF page.
      for (const r of entry.rects) {
        if (r.w <= 0 || r.h <= 0) continue;
        out.push({
          x: r.x,
          y: r.y,
          w: r.w,
          h: r.h,
          entryId: entry.id,
          label: entry.text,
          typeLabel: `OCR · ${labelForType(entry.piiType)}`,
        });
      }
    }
  }
  return out;
};

/** Find the first hotspot whose bounding box contains (xNorm, yNorm).
 *  Iterates in array order so callers can prioritise (e.g. push the
 *  most specific hotspots first). Returns undefined if none match. */
export const hitTestHotspot = (
  hotspots: readonly Hotspot[],
  xNorm: number,
  yNorm: number,
): Hotspot | undefined => {
  for (const h of hotspots) {
    if (xNorm >= h.x && xNorm <= h.x + h.w && yNorm >= h.y && yNorm <= h.y + h.h) {
      return h;
    }
  }
  return undefined;
};
